import React, { useEffect, useState } from 'react';

import { gql, useMutation } from '@apollo/client';

import { MusicNote } from '@mui/icons-material';

import { Box, Button, Dialog, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';

import { Check } from '@mui/icons-material';

import { useLocation, useNavigate } from 'react-router-dom';

import { NodeTable } from '@/components/NodeTable';

import client from '@/client';
import i18n from '@/i18n';

import { useSearchParams } from 'react-router-dom';

import { Link } from '@/components';
import { EmptyView } from '@/components/EmptyView';
import { Envelope } from '@phosphor-icons/react';
import moment from 'moment';
import { LoadingButton } from '@mui/lab';

import { HBox } from '@/components/HBox';
import { OpenInNew } from '@mui/icons-material';

const MUTATION = gql`mutation performActionOnPitches(
  $action: String!,
  $slugs: [String]!
) {
  performActionOnPitches(action: $action, slugs: $slugs) {
    pitches {
      id
      slug
    }
  }
}`

const QUERY = gql`
  query getPitches(
    $p: Int,
    $limit: Int,
    $sortBy: String,
    $status: String
  ) {
    pitch {
      pitches(
        p: $p,
        limit: $limit,
        sortBy: $sortBy,
        status: $status
      ) {
        objects {
          id
          slug
          streamUrl
          handled
          release {
            name
            upc
            artists {
              id
              name
            }
            label {
              id
              name
            }
            released
          }
          pitched
        }
      }
    }
  }
`

export function PitchesPage() {    
  const { search } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams()
  const sortBy = searchParams.get('sortBy') != "null" ? searchParams.get('sortBy') : null
  const status = searchParams.get('status') != "null" ? searchParams.get('status') : 'new'

  const [error, setError] = useState(false)
  const [confirmActionDialogOpen, setConfirmActionDialogOpen] = useState(false)
  const [checkedPitchIds, setCheckedPitchIds] = useState([])
  const [p, setPage] = useState(-1)
  const [pages, setPages] = useState(0)
  const [action, setAction] = useState(null)

  const [nodes, setNodes] = useState([])

  const [
    performActionOnPitches,
    {
      loading: performing
    }
  ] = useMutation(
    MUTATION, {
      onCompleted: () => {
        resetPage().then(() => {}).catch(e => { })
      }
    }
  )

  const handleSubmitConfirmActionDialogForm = (event) => {
    event.preventDefault()
    performActionOnPitches({
      variables: {
        action,
        slugs: checkedPitchIds
      }
    }).then(() => {
      setConfirmActionDialogOpen(false)
    })
    return false
  }

  const handleNodesChecked = ({ nodes }) => {
    setCheckedPitchIds(nodes?.map(n => n?.slug))
  }

  const handleSubmitAction = (event) => {
    event.preventDefault()
    setConfirmActionDialogOpen(true)
    return false
  } 

  const resetPage = () => {
    setPage(0)
    return fetchPage(0, true)
  }

  useEffect(() => { resetPage() }, [search])

  const fetchPage = (p, reset=false) => {
    return client.query({
      query: QUERY,
      variables: {
        sortBy,
        p: p + 1,
        limit: 100,
        status
      },
      fetchPolicy: 'no-cache'
    }).then(({ data }) => {
      let baseNodes = reset ? [] : nodes
      let objects = data.pitch.pitches.objects
      let newNodes = [...baseNodes, ...objects]

      setPages(1)
      setNodes(newNodes)
    }).catch(e => {
      setError(e)
    })
  }

  const fetchNextPage = () => {
    setPage(p + 1)
    return fetchPage(p + 1)
  }

  useEffect(() => {
    fetchNextPage()
  }, [])

  const handleTrigger = () => {
    return fetchNextPage()
  }

  return (
    <div>
      <h1>{i18n.t('pitches')}</h1>
      <div style={{ flexDirection: 'row', alignItems: 'center', display: 'flex', gap: 10 }}>
        <form onSubmit={handleSubmitAction}>
          <Box sx={{ padding: 1, display: 'flex', flexDirection: "row", width: "100%", alignItems: "center", gap: 1 }}>
            <FormControl sx={{ minWidth: 220 }}>
              <InputLabel>{i18n.t('Action')}</InputLabel>
              <Select
                value={action}
                onChange={(event) => {
                  setAction(event.target.value)
                }}
              >
                <MenuItem value={''}>{i18n.t('select-action')}</MenuItem> 
                <MenuItem value={'mark_as_handled'}>{i18n.t('mark-pitch-as-handled')}</MenuItem>
                <MenuItem value={'mark_as_unhandled'}>{i18n.t('mark-pitch-as-unhandled')}</MenuItem>
              </Select>
            </FormControl>
            <LoadingButton type="submit" variant="contained" loading={performing} disabled={checkedPitchIds.length === 0 || action === null}>{i18n.t('do')}</LoadingButton>
            <FormControl sx={{ minWidth: 220 }}>
              <InputLabel>{i18n.t('status')}</InputLabel>
              <Select
                value={status}
                onChange={(event) => {
                  setSearchParams(params => {
                    if (!event.target.value) {
                      params.delete('status')
                    } else {
                      params.set('status', event.target.value)
                    }
                    return params
                  })
                }}
              > 
                <MenuItem value={null}>{i18n.t('all')}</MenuItem>
                <MenuItem value={"new"}>{i18n.t('new')}</MenuItem>
                <MenuItem value={"handled"}>{i18n.t('handled')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </form>
      </div>
      <PitchesView 
        pitches={nodes}
        expanderEnabled={true}
        resetPage={resetPage}
        onTrigger={handleTrigger}
        onNodesChecked={handleNodesChecked}
        defaultSortByFieldId={searchParams.get('sortBy')?.replace(/-/, '')}
        defaultSortByDirection={searchParams.get('sortBy')?.startsWith('-') ? 'desc' : 'asc'}
        onSortByChanged={({ sortByFieldId, sortByDirection }) => {
          setSearchParams(params => {
            params.set('sortBy', `${sortByDirection === 'desc' ? '-' : ''}${sortByFieldId}`)
            if (!sortByFieldId) {
              params.delete('sortBy') 
            }
            return params
          })
        }}
        pages={pages}
      />
      <Dialog open={confirmActionDialogOpen} onClose={() => setConfirmActionDialogOpen(false)}>
        <DialogTitle>{i18n.t('Are you sure you want to %s %s pitches?', i18n.t(action), checkedPitchIds.length)}</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmitConfirmActionDialogForm}>
            <HBox>
              <LoadingButton
                loading={performing}
                variant="contained"
                color="error"
                type="submit"
              >
                {i18n.t('confirm %s', i18n.t(action))}
              </LoadingButton>
              <Button
                variant="secondary"
                onClick={() => setConfirmActionDialogOpen(false)}
              >
                {i18n.t('Cancel')}
              </Button>
            </HBox>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}

PitchesPage.loader = async ({ request }) => {
  const url = new URL(request.url)
  const p = url.searchParams.get('p') ? parseInt(url.searchParams.get('p')) : 0
  const limit = url.searchParams.get('limit') ? parseInt(url.searchParams.get('limit')) : 0
  const status = url.searchParams.get('status') != "null" ? searchParams.get('status') == 'true' : true

  const { data } = await client.query({
    query: QUERY,
    variables: {
      status,
      p,
      limit
    }
  })
  return data.screening
}

export function PitchesView({
  pitches,
  resetPage = () => {},
  onTrigger = () => {},
  expanderEnabled = true,
  defaultSortByFieldId,
  defaultSortByDirection,
  onNodesChecked = () => {},
  onPerformAction = () => {},
  onSortByChanged
}) {
  const [
    performActionOnPitches,
    {
      loading: performing
    }
  ] = useMutation(
    MUTATION, {
      onCompleted: () => {
        resetPage().then(() => { }).catch(e => { })
      }
    }
  )

  if (pitches?.length > 0) {
    return (
      <>
        <NodeTable
          defaultSortByFieldId={defaultSortByFieldId}
          defaultSortByDirection={defaultSortByDirection}
          onNodesChecked={onNodesChecked}
          onPerformAction={onPerformAction}
          onSortByChanged={onSortByChanged}
          expanded={true}
          getSubRows={({ node }) => {
            return []
          }}
          getRowExpanded={({ node }) => {
            return false
          }}
          getRowShowExpander={({ node }) => {
            return false
          }}
          canCheck={true}
          hasSubRows={true}
          renderRowClassNames={({ node }) => {
            return node.handled ? 'row-read' : 'row-new' // !node.uncopyrighted || !node.notMatchesArtistWatchlist || !node.originalReleaseDateMatchesCopyrightYears || !node.originalReleaseDateIsNotDifferentFromCopyrightAndPhonographicYear ? 'row-warning' : ''
          }}
          emptyHeading={i18n.t('No pitches found')}
          emptyText={i18n.t('No pitches found')}
          emptyIcon={MusicNote}

          isRowUnavailable={(row) => {
            return false        
          }}
          toolbarButtons={() => <></>}
          sx={{
            '& .row-read': {
              bgcolor: 'rgba(127, 127, 127, .1)'
            },
            '& .row-new': {
              'td': {
                fontWeight: 'bold'
              }
            }
          }}
          columns={[ 
            {
              id: 'name',
              name: i18n.t('name'),
              type: 'name',
              render: ({ node }) => {
                return (
                  <Link to={`/dashboard/pitch/${node.id}`}>{node.release.name}</Link>
                )
              }
            },
            {
              id: 'label',
              name: i18n.t('label'),
              type: 'label',
              render: ({ node }) => {
                return (
                  <Link to={`/dashboard/pitch/${node.id}`}>{node?.release?.label?.name}</Link>
                )
              }
            },
            {
              id: 'streamUrl',
              name: i18n.t('stream-url'),
              type: 'url',
              render: ({ node }) => {
                return <Link to={node.streamUrl}>{node.streamUrl}</Link>
              }
            },
            {
              id: 'pitched',
              name: i18n.t('pitched'),
              type: 'datetime',
              render: ({ node }) => {
                return moment(node.release.pitched).format('YYYY-MM-DD')
              }
            },
            {
              id: 'released',
              name: i18n.t('released'),
              type: 'datetime',
              render: ({ node }) => {
                return (
                  moment(node.release.released).format('YYYY-MM-DD')
                )
              }
            },
            {
              id: 'view',
              name: i18n.t('view'),
              type: 'button',
              render: ({ node }) => {
                if (node.handled) {
                  return (
                    <Button
                        startIcon={<Envelope />}
                        component={Link}
                        loading={performing}
                        onClick={(event) => {
                          event.preventDefault();
                          performActionOnPitches({
                            variables: {
                              action: 'mark_as_new',
                              slugs: [node.slug]
                            }
                          }).then(() => {
                          })
                          return false;
                        }}
                      >
                        {i18n.t('mark-as-new')}
                      </Button>
                  )
                } else {
                  return (
                    <Button
                      startIcon={<Check />}
                      variant="contained"
                      component={Link}
                      loading={performing}
                      onClick={(event) => {
                        event.preventDefault();
                        performActionOnPitches({
                          variables: {
                            action: 'mark_as_handled',
                            slugs: [node.slug]
                          }
                        }).then(() => {
                        })
                        return false;
                      }}
                    >
                      {i18n.t('mark-as-handled')}
                    </Button>
                  )
                }
              }
            }
          ]}
          renderSubRows={({ node, columns }) => {
            const streamUrl = node.streamUrl // ?? "https://example.com"

            return (
              <>  
                <tr>
                  <td></td>
                  <td colSpan={columns.length + 2}>
                    <Typography sx={{ opacity: 0.5, textTransform: 'uppercase' }}>{i18n.t('press-text')}</Typography>
                    <Typography>{node.description ?? '-'}</Typography>
                    <Button
                      endIcon={<OpenInNew />}
                      disabled={!streamUrl}
                      variant="contained"
                      component={Link}
                      onClick={(event) => {
                        event.preventDefault();
                        window.open(streamUrl);
                        return false;
                      }}
                    >
                      {i18n.t('listen')}
                    </Button>
                  </td>
                </tr>
              </>
            )
          }}
          onTrigger={onTrigger}
          expanderEnabled={expanderEnabled}
          nodes={pitches}
        />
      </>
    )
  } else {
    return (
      <EmptyView
        heading={i18n.t('no-pitches-found')}
      />       
    )
  }
}

export default PitchesPage